import React from 'react';
import { Box, Flex, HStack, VStack, Skeleton, SkeletonCircle } from '@chakra-ui/react';
import { PALETTES, useAppColorMode } from './appColors';

// Full app-shell skeleton shown while the app boots after login: header,
// sidebar with link rows, and the content area to the right — the way large
// apps reveal their chrome before data lands. Honours the saved colour mode.
const AppSkeleton = () => {
  const { isDark } = useAppColorMode();
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const HEADER_H = 54;
  const SIDEBAR_W = 205;

  // Shared shimmer colours so every placeholder pulses as one system.
  const sk = {
    startColor: p.surfaceSubtle,
    endColor: p.border,
    speed: 1.6,
  };

  return (
    <Box
      className="stande-app"
      data-theme={isDark ? 'dark' : 'light'}
      position="fixed"
      inset="0"
      zIndex="2000"
      bg={p.page}
      overflow="hidden"
    >
      {/* Header */}
      <Flex
        position="fixed"
        top="0"
        left="0"
        right="0"
        h={`${HEADER_H}px`}
        align="center"
        px="14px"
        gap="24px"
        bg={p.chrome}
        borderBottom="1.5px solid"
        borderColor={p.border}
      >
        <Skeleton {...sk} h="20px" w="82px" borderRadius="4px" />
        <Skeleton {...sk} h="37px" flex="1" maxW="740px" borderRadius="24px" />
        <HStack spacing="12px" ml="auto">
          <SkeletonCircle {...sk} size="8" />
          <SkeletonCircle {...sk} size="8" />
          <SkeletonCircle {...sk} size="8" />
        </HStack>
      </Flex>

      {/* Sidebar */}
      <Box
        position="fixed"
        top={`${HEADER_H}px`}
        left="0"
        h={`calc(100vh - ${HEADER_H}px)`}
        w={`${SIDEBAR_W}px`}
        bg={p.chrome}
        borderRight="1px solid"
        borderColor={p.border}
        px="16px"
        pt="28px"
      >
        <Skeleton {...sk} h="12px" w="64px" borderRadius="4px" mb="22px" />
        <VStack align="stretch" spacing="16px">
          {Array.from({ length: 6 }).map((_, i) => (
            <HStack key={i} spacing="12px">
              <SkeletonCircle {...sk} size="6" />
              <Skeleton {...sk} h="12px" w={`${110 - i * 8}px`} borderRadius="4px" />
            </HStack>
          ))}
        </VStack>

        {/* Bottom cluster: upgrade + settings/logout */}
        <VStack align="stretch" spacing="14px" position="absolute" bottom="24px" left="16px" right="16px">
          <Skeleton {...sk} h="40px" w="100%" borderRadius="12px" />
          <HStack spacing="12px">
            <SkeletonCircle {...sk} size="6" />
            <Skeleton {...sk} h="12px" w="80px" borderRadius="4px" />
          </HStack>
          <HStack spacing="12px">
            <SkeletonCircle {...sk} size="6" />
            <Skeleton {...sk} h="12px" w="64px" borderRadius="4px" />
          </HStack>
        </VStack>
      </Box>

      {/* Content */}
      <Box
        position="absolute"
        top={`${HEADER_H}px`}
        left={`${SIDEBAR_W}px`}
        right="0"
        bottom="0"
        px={{ base: 6, md: 8 }}
        pt="45px"
      >
        {/* Greeting */}
        <Skeleton {...sk} h="30px" w="260px" borderRadius="6px" mb="34px" />

        {/* Performance card */}
        <Skeleton {...sk} h="114px" w="245px" borderRadius="20px" mb="44px" />

        {/* Two content panels */}
        <Flex direction={{ base: 'column', md: 'row' }} gap="32px">
          <Skeleton {...sk} h="400px" w={{ base: '100%', md: '50%' }} borderRadius="14px" />
          <Skeleton {...sk} h="400px" w={{ base: '100%', md: '50%' }} borderRadius="14px" />
        </Flex>
      </Box>
    </Box>
  );
};

export default AppSkeleton;
